import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import { createInclusion, loadEmployeeUser, masters, uploadToModule } from "./api";
import { DECLARATION_TEXT, DOC_TYPE, FALLBACK_ADDITION_OF, MANDATORY_DOCUMENTS } from "./constants";
import {
  emptySlot,
  getOccupationCategory,
  employeeProfile,
  getOccupationDetailField,
  isJpeg,
  isPdf,
  isSupportedDoc,
  phoneOk,
  toISODate,
  toSapAmount,
  type Option,
  type UploadSlot,
  type UploadedDoc,
} from "./helpers";
import {
  ActionBar,
  AlertModal,
  DateField,
  DocUploader,
  Field,
  PageShell,
  RadioGroup,
  Section,
  SelectInput,
  TextArea,
  TextInput,
  UploadSlotButton,
} from "./ui";

type Alive = {
  firstName: string;
  lastName: string;
  dob: string;
  currentAddress: string;
  occupation: string;
  detailsOfEmployer: string;
  monthlyIncome: string;
};
type Dead = {
  firstName: string;
  lastName: string;
  dob: string;
  occupationBeforeDeath: string;
  detailsOfEmployer: string;
  dateOfDeath: string;
};
type Sibling = {
  id: string;
  name: string;
  dob: string;
  relationship: string;
  workingWith: string;
  detailsOfEmployer: string;
  empId: string;
  remark: string;
};

const blankAlive = (): Alive => ({
  firstName: "",
  lastName: "",
  dob: "",
  currentAddress: "",
  occupation: "",
  detailsOfEmployer: "",
  monthlyIncome: "",
});
const blankDead = (): Dead => ({
  firstName: "",
  lastName: "",
  dob: "",
  occupationBeforeDeath: "",
  detailsOfEmployer: "",
  dateOfDeath: "",
});

const MODULE = "inclusion_of_dependents";

const InclusionParentsForm = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    employeeNo: "",
    employeeName: "",
    designation: "",
    payScale: "",
    basicPay: "",
    contactNo: "",
    spouseName: "",
    additionOf: "",
    remarks: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [father, setFather] = useState<Alive>(blankAlive());
  const [mother, setMother] = useState<Alive>(blankAlive());
  const [deceasedFather, setDeceasedFather] = useState<Dead>(blankDead());
  const [deceasedMother, setDeceasedMother] = useState<Dead>(blankDead());
  const [fatherPhoto, setFatherPhoto] = useState<UploadSlot>(emptySlot());
  const [fatherId, setFatherId] = useState<UploadSlot>(emptySlot());
  const [fatherResidency, setFatherResidency] = useState<UploadSlot>(emptySlot());
  const [fatherIncome, setFatherIncome] = useState<UploadSlot>(emptySlot());
  const [motherPhoto, setMotherPhoto] = useState<UploadSlot>(emptySlot());
  const [motherId, setMotherId] = useState<UploadSlot>(emptySlot());
  const [motherResidency, setMotherResidency] = useState<UploadSlot>(emptySlot());
  const [motherIncome, setMotherIncome] = useState<UploadSlot>(emptySlot());
  const [motherWidowPension, setMotherWidowPension] = useState<UploadSlot>(emptySlot());
  const [deceasedFatherDeathCert, setDeceasedFatherDeathCert] = useState<UploadSlot>(emptySlot());
  const [deceasedMotherDeathCert, setDeceasedMotherDeathCert] = useState<UploadSlot>(emptySlot());
  const [spouseOccupation, setSpouseOccupation] = useState("");
  const [spouseEmployeeId, setSpouseEmployeeId] = useState("");
  const [spouseNoc, setSpouseNoc] = useState<UploadSlot>(emptySlot());
  const [spouseUndertaking, setSpouseUndertaking] = useState<UploadSlot>(emptySlot());
  const [siblings, setSiblings] = useState<Sibling[]>([]);
  const [isSingleChild, setIsSingleChild] = useState<string | null>(null);
  const [siblingNocCertificate, setSiblingNocCertificate] = useState<string | null>(null);
  const [siblingUndertaking, setSiblingUndertaking] = useState<UploadSlot>(emptySlot());
  const [siblingNocFile, setSiblingNocFile] = useState<UploadSlot>(emptySlot());
  const [postRetirementBenefit, setPostRetirementBenefit] = useState<string | null>(null);
  const [noMedical, setNoMedical] = useState<UploadSlot>(emptySlot());
  const [declarationChecked, setDeclarationChecked] = useState(false);
  const [uploadedDocs, setUploadedDocs] = useState<UploadedDoc[]>([]);
  const [additionOfOptions, setAdditionOfOptions] = useState<Option[]>(FALLBACK_ADDITION_OF);
  const [occupationOptions, setOccupationOptions] = useState<Option[]>([]);
  const [spouseOccupationOptions, setSpouseOccupationOptions] = useState<Option[]>([]);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState<"ltc" | "postRetirement" | null>(null);

  useEffect(() => {
    loadEmployeeUser().then((user) => {
      const p = employeeProfile(user);
      setForm((prev) => ({
        ...prev,
        employeeNo: p.employeeNo,
        employeeName: p.employeeName,
        designation: p.designation,
        payScale: p.payScale,
        basicPay: p.basicPay,
        contactNo: prev.contactNo || p.contactNo,
        spouseName: p.spouseName,
      }));
    });
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [a, o, s] = await Promise.all([masters.additionOf(), masters.occupation(), masters.siblingOccupation()]);
        if (a.length) setAdditionOfOptions(a);
        setOccupationOptions(o);
        setSpouseOccupationOptions(s);
      } catch {
        /* keep fallbacks */
      }
    })();
  }, []);

  const namedUpload = async (
    file: File,
    setter: React.Dispatch<React.SetStateAction<UploadSlot>>,
    isPhoto = false
  ) => {
    if (isPhoto ? !isJpeg(file) : !isSupportedDoc(file)) {
      toast.error(isPhoto ? "Only JPEG/JPG files are allowed" : "Only PDF, JPG or PNG files are allowed");
      return;
    }
    if (isPhoto) {
      const kb = file.size / 1024;
      if (kb < 20 || kb > 50) {
        toast.error("Passport size photo must be between 20KB to 50KB.");
        return;
      }
    }
    const mime = !isPhoto && isPdf(file) ? "application/pdf" : file.type || "application/octet-stream";
    setter((prev) => ({ ...prev, isUploading: true }));
    try {
      const res = await uploadToModule(file, MODULE, mime);
      if (res.ok) {
        setter({
          fileName: file.name,
          fileExtension: !isPhoto && isPdf(file) ? mime : file.type || "image/jpeg",
          key: res.key,
          isUploading: false,
        });
        toast.success("File uploaded successfully");
      } else {
        setter((prev) => ({ ...prev, isUploading: false }));
        toast.error(res.message || "Something went wrong");
      }
    } catch {
      setter((prev) => ({ ...prev, isUploading: false }));
      toast.error("Something went wrong");
    }
  };

  const buildDoc = (slot: UploadSlot, typeOfDocument: string) =>
    slot.key
      ? [{ FileName: slot.fileName || "", FileExtension: slot.fileExtension || "", TypeOfDocument: typeOfDocument, key: slot.key }]
      : [];

  const validate = () => {
    const e: Record<string, string> = {};
    const missing: string[] = [];
    if (!form.employeeNo.trim()) missing.push((e.employeeNo = "Employee No is required"));
    if (!form.employeeName.trim()) missing.push((e.employeeName = "Employee Name is required"));
    if (!form.payScale.trim()) missing.push((e.payScale = "Pay Scale is required"));
    if (!form.designation.trim()) missing.push((e.designation = "Designation is required"));
    if (!form.contactNo.trim()) missing.push((e.contactNo = "Contact No is required"));
    else if (!phoneOk(form.contactNo))
      missing.push((e.contactNo = "Invalid phone number (10-15 digits, optional +)"));
    if (!form.basicPay.trim() || !Number.isFinite(parseFloat(form.basicPay)))
      missing.push((e.basicPay = "Basic Pay is required"));
    if (!form.additionOf) missing.push((e.additionOf = "Addition Of is required"));
    if (!isSingleChild) missing.push("Please select whether employee is a single child");
    if (!postRetirementBenefit) missing.push("Please select the post-retirement medical benefit option");
    if (isSingleChild === "No" && !siblingNocCertificate) missing.push("Please select the sibling NOC certificate option");
    if (!declarationChecked) missing.push("Please accept the declaration before submitting");
    setErrors(e);
    return missing;
  };

  const handleSave = async () => {
    const missing = validate();
    if (missing.length) {
      toast.error(missing.join("\n"));
      return;
    }
    setSaving(true);
    try {
      const toDocument = [
        ...buildDoc(noMedical, DOC_TYPE.NOT_AVAILING_MEDICAL_FACILITY),
        ...buildDoc(fatherPhoto, DOC_TYPE.FATHER_PHOTO),
        ...buildDoc(fatherId, DOC_TYPE.FATHER_ID),
        ...buildDoc(fatherResidency, DOC_TYPE.FATHER_RESIDENCY),
        ...buildDoc(fatherIncome, DOC_TYPE.FATHER_INCOME),
        ...buildDoc(motherPhoto, DOC_TYPE.MOTHER_PHOTO),
        ...buildDoc(motherId, DOC_TYPE.MOTHER_ID),
        ...buildDoc(motherResidency, DOC_TYPE.MOTHER_RESIDENCY),
        ...buildDoc(motherIncome, DOC_TYPE.MOTHER_INCOME),
        ...buildDoc(motherWidowPension, DOC_TYPE.WIDOW_PENSION_ORDER),
        ...buildDoc(deceasedFatherDeathCert, DOC_TYPE.DECEASED_FATHER_DEATH_CERT),
        ...buildDoc(deceasedMotherDeathCert, DOC_TYPE.DECEASED_MOTHER_DEATH_CERT),
        ...buildDoc(spouseNoc, DOC_TYPE.SPOUSE_NOC_CERTIFICATE),
        ...buildDoc(spouseUndertaking, DOC_TYPE.SPOUSE_DMRC_UNDERTAKING),
        ...buildDoc(siblingUndertaking, DOC_TYPE.SIBLING_DMRC_UNDERTAKING),
        ...buildDoc(siblingNocFile, DOC_TYPE.SIBLING_NOC_CERTIFICATE_FROM_ORG),
        ...uploadedDocs.map((d) => ({
          FileName: d.fileName || "",
          FileExtension: d.fileExtension || "",
          TypeOfDocument: DOC_TYPE.OTHER,
          key: d.key,
        })),
      ];
      const spouseLabel = spouseOccupationOptions.find((o) => o.id === spouseOccupation)?.label || "";
      const spouseCategory = getOccupationCategory(spouseLabel);
      const dmrcSiblings = siblings.filter(
        (s) => getOccupationCategory(spouseOccupationOptions.find((o) => o.id === s.workingWith)?.label || "") === "dmrc"
      );
      const isSpouseOrSiblingInDmrc = spouseCategory === "dmrc" || dmrcSiblings.length > 0;
      const payload: Record<string, any> = {
        Action: "C",
        ReferenceNumber: "",
        EmpID: form.employeeNo,
        Bomof: form.additionOf,
        ContactNumber: form.contactNo,
        Remarks: form.remarks,
        IfEmployeeSingleChild: isSingleChild === "Yes" ? "Y" : "N",
        IfPostRetirement: postRetirementBenefit === "Yes" ? "Y" : postRetirementBenefit === "No" ? "N" : "",
        IfNOCFromSibling:
          isSingleChild === "No" ? (siblingNocCertificate === "Yes" ? "Y" : siblingNocCertificate === "No" ? "N" : "") : "",
        IfSpouseSiblingInDMRC: isSpouseOrSiblingInDmrc ? "Y" : "N",
        EmpIDOfSiblingSpouseInDMRC: spouseCategory === "dmrc" ? spouseEmployeeId : dmrcSiblings[0]?.empId || "",
        NameOfSiblingSpouseInDMRC: spouseCategory === "dmrc" ? form.spouseName : dmrcSiblings[0]?.name || "",
        SpouseOccupation: spouseOccupation,
        ToDocument: { results: toDocument },
      };
      if (isSingleChild === "No") {
        payload.ToSiblings = {
          results: siblings.map((s, idx) => ({
            SequenceNumber: String(idx + 1).padStart(2, "0"),
            Name: s.name,
            DateOfBirth: toISODate(s.dob),
            Relationship: s.relationship,
            WorkingWith: s.workingWith,
            EmpIDOfSibling: s.empId,
            DetailsOfEmployer: s.detailsOfEmployer,
            Remark: s.remark,
          })),
        };
      }
      if (form.additionOf === "00" || form.additionOf === "11") {
        payload.FatherFirstName = father.firstName;
        payload.FatherLastName = father.lastName;
        payload.FatherDateOfBirth = toISODate(father.dob);
        payload.FatherCurrentAddress = father.currentAddress;
        payload.FatherOccupation = father.occupation;
        payload.FatherDetailsOfEmployer = father.detailsOfEmployer;
        payload.FatherMonthlyIncome = toSapAmount(father.monthlyIncome);
      }
      if (form.additionOf === "00" || form.additionOf === "12") {
        payload.MotherFirstName = mother.firstName;
        payload.MotherLastName = mother.lastName;
        payload.MotherDateOfBirth = toISODate(mother.dob);
        payload.MotherCurrentAddress = mother.currentAddress;
        payload.MotherOccupation = mother.occupation;
        payload.MotherDetailsOfEmployer = mother.detailsOfEmployer;
        payload.MotherMonthlyIncome = toSapAmount(mother.monthlyIncome);
      }
      if (form.additionOf === "12") {
        payload.FatherFirstName = deceasedFather.firstName;
        payload.FatherLastName = deceasedFather.lastName;
        payload.FatherDateOfBirth = toISODate(deceasedFather.dob);
        payload.FatherOccupation = deceasedFather.occupationBeforeDeath;
        payload.FatherDetailsOfEmployer = deceasedFather.detailsOfEmployer;
        payload.FatherDeathDate = toISODate(deceasedFather.dateOfDeath);
      }
      if (form.additionOf === "11") {
        payload.MotherFirstName = deceasedMother.firstName;
        payload.MotherLastName = deceasedMother.lastName;
        payload.MotherDateOfBirth = toISODate(deceasedMother.dob);
        payload.MotherOccupation = deceasedMother.occupationBeforeDeath;
        payload.MotherDetailsOfEmployer = deceasedMother.detailsOfEmployer;
        payload.MotherDeathDate = toISODate(deceasedMother.dateOfDeath);
      }
      const res = await createInclusion(payload);
      if (res?.success) {
        toast.success(res?.message || "Request Submitted Successfully.");
        navigate(all_routes.idCard.inclusionOfDependent);
      } else toast.error(res?.message || "Something went wrong");
    } catch {
      toast.error("Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const occLabel = (id: string, opts: Option[]) => opts.find((o) => o.id === id)?.label || "";

  const aliveBlock = (
    title: string,
    who: Alive,
    setWho: React.Dispatch<React.SetStateAction<Alive>>,
    photo: UploadSlot,
    setPhoto: React.Dispatch<React.SetStateAction<UploadSlot>>,
    idSlot: UploadSlot,
    setId: React.Dispatch<React.SetStateAction<UploadSlot>>,
    res: UploadSlot,
    setRes: React.Dispatch<React.SetStateAction<UploadSlot>>,
    inc: UploadSlot,
    setInc: React.Dispatch<React.SetStateAction<UploadSlot>>,
    photoCode: string,
    idCode: string,
    resCode: string,
    incCode: string,
    extra?: React.ReactNode
  ) => {
    const detail = getOccupationDetailField(occLabel(who.occupation, occupationOptions));
    return (
      <Section title={title}>
        <Field label="First Name" required>
          <TextInput value={who.firstName} placeholder="Enter First Name" onChange={(e) => setWho({ ...who, firstName: e.target.value })} />
        </Field>
        <Field label="Last Name">
          <TextInput value={who.lastName} placeholder="Enter Last Name" onChange={(e) => setWho({ ...who, lastName: e.target.value })} />
        </Field>
        <Field label="Date Of Birth" required>
          <DateField value={who.dob} onChange={(v) => setWho({ ...who, dob: v })} />
        </Field>
        <Field label="Current Address" required>
          <TextInput value={who.currentAddress} placeholder="Enter current address" onChange={(e) => setWho({ ...who, currentAddress: e.target.value })} />
        </Field>
        <Field label="Occupation / Source of Income" required>
          <SelectInput value={who.occupation} placeholder="Select an option" options={occupationOptions} onChange={(v) => setWho({ ...who, occupation: v })} />
        </Field>
        {detail.visible && (
          <Field label={detail.label}>
            <TextInput value={who.detailsOfEmployer} placeholder="Enter details of employer" onChange={(e) => setWho({ ...who, detailsOfEmployer: e.target.value })} />
          </Field>
        )}
        <Field label="Monthly Income" required>
          <TextInput value={who.monthlyIncome} placeholder="Enter monthly income" onChange={(e) => setWho({ ...who, monthlyIncome: e.target.value })} />
        </Field>
        <UploadSlotButton required label="Upload latest photo" slot={photo} onPick={(f) => namedUpload(f, setPhoto, true)} onClear={() => setPhoto(emptySlot())} />
        <UploadSlotButton required label="Upload ID Proof" slot={idSlot} onPick={(f) => namedUpload(f, setId)} onClear={() => setId(emptySlot())} />
        <UploadSlotButton required label="Upload Residency Proof" slot={res} onPick={(f) => namedUpload(f, setRes)} onClear={() => setRes(emptySlot())} />
        <UploadSlotButton required label="Upload Income Proof" slot={inc} onPick={(f) => namedUpload(f, setInc)} onClear={() => setInc(emptySlot())} />
        {extra}
        <span className="d-none">{photoCode}{idCode}{resCode}{incCode}</span>
      </Section>
    );
  };

  const deadBlock = (
    title: string,
    who: Dead,
    setWho: React.Dispatch<React.SetStateAction<Dead>>,
    cert: UploadSlot,
    setCert: React.Dispatch<React.SetStateAction<UploadSlot>>
  ) => {
    const detail = getOccupationDetailField(occLabel(who.occupationBeforeDeath, occupationOptions), true);
    return (
      <Section title={title}>
        <Field label="First Name" required>
          <TextInput value={who.firstName} placeholder="Enter First Name" onChange={(e) => setWho({ ...who, firstName: e.target.value })} />
        </Field>
        <Field label="Last Name">
          <TextInput value={who.lastName} placeholder="Enter Last Name" onChange={(e) => setWho({ ...who, lastName: e.target.value })} />
        </Field>
        <Field label="Date Of Birth">
          <DateField value={who.dob} onChange={(v) => setWho({ ...who, dob: v })} />
        </Field>
        <Field label="Occupation before death" required>
          <SelectInput value={who.occupationBeforeDeath} placeholder="Select an option" options={occupationOptions} onChange={(v) => setWho({ ...who, occupationBeforeDeath: v })} />
        </Field>
        {detail.visible && (
          <Field label={detail.label}>
            <TextInput value={who.detailsOfEmployer} placeholder="Enter details of employer" onChange={(e) => setWho({ ...who, detailsOfEmployer: e.target.value })} />
          </Field>
        )}
        <Field label="Date of death" required>
          <DateField value={who.dateOfDeath} onChange={(v) => setWho({ ...who, dateOfDeath: v })} />
        </Field>
        <UploadSlotButton required label="Upload Death Certificate" slot={cert} onPick={(f) => namedUpload(f, setCert)} onClear={() => setCert(emptySlot())} />
      </Section>
    );
  };

  const spouseCategory = getOccupationCategory(occLabel(spouseOccupation, spouseOccupationOptions));
  const bomof = form.additionOf;

  return (
    <PageShell title="Inclusion and Deletion" subtitle="Application for Inclusion of Dependent Parents Including Widow Mother">
      <div className="df-note">
        <div className="fw-bold mb-2">Mandatory Documents</div>
        <div style={{ whiteSpace: "pre-wrap", color: "#000", fontSize: 14 }}>{MANDATORY_DOCUMENTS}</div>
      </div>

      <Section title="Employee Details">
        <Field label="Employee No." error={errors.employeeNo}>
          <TextInput value={form.employeeNo} readOnly disabled />
        </Field>
        <Field label="Employee Name" error={errors.employeeName}>
          <TextInput value={form.employeeName} readOnly disabled />
        </Field>
        <Field label="Pay Scale" error={errors.payScale}>
          <TextInput value={form.payScale} readOnly disabled />
        </Field>
        <Field label="Designation" error={errors.designation}>
          <TextInput value={form.designation} readOnly disabled />
        </Field>
        <Field label="Contact No." required error={errors.contactNo}>
          <TextInput value={form.contactNo} placeholder="Enter Contact Number" onChange={(e) => setForm({ ...form, contactNo: e.target.value })} />
        </Field>
        <Field label="Basic Pay" error={errors.basicPay}>
          <TextInput value={form.basicPay} readOnly disabled />
        </Field>
        <Field label="Addition Of" required error={errors.additionOf}>
          <SelectInput value={form.additionOf} placeholder="Select an option" options={additionOfOptions} onChange={(v) => setForm({ ...form, additionOf: v })} />
        </Field>
      </Section>

      {(bomof === "00" || bomof === "11") &&
        aliveBlock("Details of Father", father, setFather, fatherPhoto, setFatherPhoto, fatherId, setFatherId, fatherResidency, setFatherResidency, fatherIncome, setFatherIncome, DOC_TYPE.FATHER_PHOTO, DOC_TYPE.FATHER_ID, DOC_TYPE.FATHER_RESIDENCY, DOC_TYPE.FATHER_INCOME)}
      {bomof === "11" && deadBlock("Details of deceased mother", deceasedMother, setDeceasedMother, deceasedMotherDeathCert, setDeceasedMotherDeathCert)}
      {bomof === "12" && deadBlock("Details of deceased father", deceasedFather, setDeceasedFather, deceasedFatherDeathCert, setDeceasedFatherDeathCert)}
      {(bomof === "00" || bomof === "12") &&
        aliveBlock(
          "Details of Mother",
          mother,
          setMother,
          motherPhoto,
          setMotherPhoto,
          motherId,
          setMotherId,
          motherResidency,
          setMotherResidency,
          motherIncome,
          setMotherIncome,
          DOC_TYPE.MOTHER_PHOTO,
          DOC_TYPE.MOTHER_ID,
          DOC_TYPE.MOTHER_RESIDENCY,
          DOC_TYPE.MOTHER_INCOME,
          bomof === "12" ? (
            <UploadSlotButton
              label="Upload Widow pension pay order & ITR in case of widow mother"
              slot={motherWidowPension}
              onPick={(f) => namedUpload(f, setMotherWidowPension)}
              onClear={() => setMotherWidowPension(emptySlot())}
            />
          ) : null
        )}

      <DocUploader
        title="Any other documents (If required)"
        docs={uploadedDocs}
        onAdd={async (file) => {
          if (!isSupportedDoc(file)) {
            toast.error("Only PDF, JPG or PNG files are allowed");
            return;
          }
          const mime = isPdf(file) ? "application/pdf" : file.type || "application/octet-stream";
          const res = await uploadToModule(file, MODULE, mime);
          if (res.ok) {
            setUploadedDocs((prev) => [...prev, { fileName: file.name, fileExtension: mime, fileSize: file.size, key: res.key }]);
          } else toast.error(res.message || "Something went wrong");
        }}
        onRemove={(i) => setUploadedDocs((prev) => prev.filter((_, idx) => idx !== i))}
      />

      {bomof !== "" && (
        <Section title="Spouse Details">
          <Field label="Name of Spouse" required>
            <TextInput value={form.spouseName} readOnly disabled />
          </Field>
          <Field label="Spouse Occupation" required>
            <SelectInput value={spouseOccupation} placeholder="Select an option" options={spouseOccupationOptions} onChange={setSpouseOccupation} />
          </Field>
          {spouseCategory === "govt_psu" && (
            <UploadSlotButton
              label="Upload Certificate for no claim for medical/LTC facilities by parents from Spouse's Organization"
              slot={spouseNoc}
              onPick={(f) => namedUpload(f, setSpouseNoc)}
              onClear={() => setSpouseNoc(emptySlot())}
            />
          )}
          {spouseCategory === "dmrc" && (
            <>
              <Field label="Spouse employee ID">
                <TextInput value={spouseEmployeeId} placeholder="Enter spouse employee ID" onChange={(e) => setSpouseEmployeeId(e.target.value)} />
              </Field>
              <UploadSlotButton
                required
                label="Upload Undertaking from Spouse working in DMRC"
                slot={spouseUndertaking}
                onPick={(f) => namedUpload(f, setSpouseUndertaking)}
                onClear={() => setSpouseUndertaking(emptySlot())}
              />
            </>
          )}
        </Section>
      )}

      <Section>
        <div className="fw-bold mb-2" style={{ color: "#2B2E38" }}>
          Whether employee is a single child?<span style={{ color: "#FF0F0F" }}> *</span>
        </div>
        <RadioGroup value={isSingleChild} options={["Yes", "No"]} onChange={setIsSingleChild} />
      </Section>

      {isSingleChild === "No" && (
        <Section title="Details of Siblings">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span />
            <button
              type="button"
              className="btn btn-sm"
              style={{ color: "#C0282C" }}
              onClick={() =>
                setSiblings((prev) => [
                  ...prev,
                  { id: `${Date.now()}`, name: "", dob: "", relationship: "", workingWith: "", detailsOfEmployer: "", empId: "", remark: "" },
                ])
              }
            >
              <i className="ti ti-circle-plus" /> Add Sibling
            </button>
          </div>
          {siblings.length === 0 && <div style={{ color: "#8A93A8" }}>No siblings added yet</div>}
          {siblings.map((s, index) => (
            <div key={s.id} className="df-fields p-3 mb-2 bg-white" style={{ border: "1px solid #E2E8F0", borderRadius: 8 }}>
              <div className="d-flex justify-content-between">
                <strong>Sibling {index + 1}</strong>
                <button type="button" className="btn btn-link text-danger p-0" onClick={() => setSiblings((prev) => prev.filter((x) => x.id !== s.id))}>
                  <i className="ti ti-trash" />
                </button>
              </div>
              <Field label="Name" required>
                <TextInput value={s.name} placeholder="Enter Name" onChange={(e) => setSiblings((prev) => prev.map((x) => (x.id === s.id ? { ...x, name: e.target.value } : x)))} />
              </Field>
              <Field label="Date Of Birth" required>
                <DateField value={s.dob} onChange={(v) => setSiblings((prev) => prev.map((x) => (x.id === s.id ? { ...x, dob: v } : x)))} />
              </Field>
              <Field label="Relationship" required>
                <TextInput value={s.relationship} placeholder="Enter relationship" onChange={(e) => setSiblings((prev) => prev.map((x) => (x.id === s.id ? { ...x, relationship: e.target.value } : x)))} />
              </Field>
              <Field label="Working With" required>
                <SelectInput value={s.workingWith} placeholder="Select an option" options={spouseOccupationOptions} onChange={(v) => setSiblings((prev) => prev.map((x) => (x.id === s.id ? { ...x, workingWith: v } : x)))} />
              </Field>
              <Field label="Details of employer">
                <TextInput value={s.detailsOfEmployer} placeholder="Enter details of employer" onChange={(e) => setSiblings((prev) => prev.map((x) => (x.id === s.id ? { ...x, detailsOfEmployer: e.target.value } : x)))} />
              </Field>
              <Field label="Employee ID (if working with DMRC)">
                <TextInput value={s.empId} placeholder="Enter Employee Id" onChange={(e) => setSiblings((prev) => prev.map((x) => (x.id === s.id ? { ...x, empId: e.target.value } : x)))} />
              </Field>
              <Field label="Remarks">
                <TextInput value={s.remark} placeholder="Enter Remarks" onChange={(e) => setSiblings((prev) => prev.map((x) => (x.id === s.id ? { ...x, remark: e.target.value } : x)))} />
              </Field>
            </div>
          ))}
          {siblings.some((s) => getOccupationCategory(occLabel(s.workingWith, spouseOccupationOptions)) === "dmrc") && (
            <UploadSlotButton
              required
              label="Upload Undertaking from Sibling working in DMRC"
              slot={siblingUndertaking}
              onPick={(f) => namedUpload(f, setSiblingUndertaking)}
              onClear={() => setSiblingUndertaking(emptySlot())}
            />
          )}
          <div className="fw-bold mb-2">
            Certificate of No claim of LTC/Medical for parents from siblings&apos; employer attached
            <span style={{ color: "#FF0F0F" }}> *</span>
          </div>
          <RadioGroup
            column
            value={siblingNocCertificate}
            options={["Yes", "No", "Not Applicable"]}
            onChange={(val) => {
              setSiblingNocCertificate(val);
              if (val === "No" && postRetirementBenefit === "Yes") setModal("ltc");
            }}
          />
          {siblingNocCertificate === "Yes" && (
            <div className="mt-3">
              <UploadSlotButton
                label="Upload Certificate for no claim for medical/LTC facilities by parents from Sibling's Organization"
                slot={siblingNocFile}
                onPick={(f) => namedUpload(f, setSiblingNocFile)}
                onClear={() => setSiblingNocFile(emptySlot())}
              />
            </div>
          )}
        </Section>
      )}

      <Section>
        <div className="fw-bold" style={{ color: "#2B2E38" }}>
          Whether any post retirement medical benefit is being availed by parents of employee
          <span style={{ color: "#FF0F0F" }}> *</span>
        </div>
        <div className="mb-2" style={{ color: "#8A93A8", fontSize: 13 }}>
          Applicable for parents in Govt./PSU/Semi Govt./Local bodies etc.
        </div>
        <RadioGroup
          column
          value={postRetirementBenefit}
          options={["Yes", "No", "Not Applicable"]}
          onChange={(val) => {
            setPostRetirementBenefit(val);
            if (val === "Yes") setModal("postRetirement");
          }}
        />
        {postRetirementBenefit === "No" && (
          <div className="mt-3">
            <UploadSlotButton
              label="Upload Certificate that medical facilities are not available to parents of the employee"
              slot={noMedical}
              onPick={(f) => namedUpload(f, setNoMedical)}
              onClear={() => setNoMedical(emptySlot())}
            />
          </div>
        )}
      </Section>

      <Section title="Remarks">
        <TextArea value={form.remarks} placeholder="Enter Remarks" onChange={(e) => setForm({ ...form, remarks: e.target.value })} />
      </Section>

      <Section>
        <label className="d-flex gap-2" style={{ color: "#000" }}>
          <input type="checkbox" checked={declarationChecked} onChange={(e) => setDeclarationChecked(e.target.checked)} />
          <span>{DECLARATION_TEXT}</span>
        </label>
      </Section>

      <ActionBar
        left="Cancel"
        right="Submit"
        busy={saving}
        onLeft={() => navigate(all_routes.idCard.inclusionOfDependent)}
        onRight={handleSave}
      />

      <AlertModal
        open={modal !== null}
        title="Addition of Dependent Parents"
        message={
          modal === "postRetirement"
            ? "Application cannot be processed if Post Retirement Medical benefit is being availed by parents of employee"
            : "Application cannot be processed if Certificate of No claim of LTC/Medical for parents from siblings' employer is not attached."
        }
        onOk={() => {
          if (modal === "ltc") setSiblingNocCertificate(null);
          else if (modal === "postRetirement") setPostRetirementBenefit(null);
          setModal(null);
        }}
      />
    </PageShell>
  );
};

export default InclusionParentsForm;
